import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Schedule, Garden, ZoneScheduleAssignment } from '@/types';
import { mockSchedules } from '@/data/mockData';
import { Calendar, Clock, Timer, Check, MapPin, Layers } from 'lucide-react';

interface MassScheduleAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gardens: Garden[];
  onAssign: (updatedGardens: Garden[]) => void;
}

const DAYS_OF_WEEK = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

interface ZoneSelection {
  gardenId: string;
  zoneId: string;
}

export function MassScheduleAssignDialog({
  open,
  onOpenChange,
  gardens,
  onAssign,
}: MassScheduleAssignDialogProps) {
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<Set<string>>(new Set());
  const [selectedZones, setSelectedZones] = useState<Set<string>>(new Set()); // "gardenId:zoneId"

  // Only show active schedules
  const activeSchedules = mockSchedules.filter((s) => s.enabled);

  // Count zones that will be affected
  const affectedZonesCount = selectedZones.size;

  const formatDays = (daysOfWeek: number[]) => {
    if (daysOfWeek.length === 7) return 'Todos los días';
    if (
      daysOfWeek.length === 5 &&
      !daysOfWeek.includes(0) &&
      !daysOfWeek.includes(6)
    )
      return 'L-V';
    if (
      daysOfWeek.length === 2 &&
      daysOfWeek.includes(0) &&
      daysOfWeek.includes(6)
    )
      return 'Fin de semana';
    return daysOfWeek.map((d) => DAYS_OF_WEEK[d]).join(', ');
  };

  const handleToggleSchedule = (scheduleId: string) => {
    const newSelected = new Set(selectedScheduleIds);
    if (newSelected.has(scheduleId)) {
      newSelected.delete(scheduleId);
    } else {
      newSelected.add(scheduleId);
    }
    setSelectedScheduleIds(newSelected);
  };

  const handleToggleZone = (gardenId: string, zoneId: string) => {
    const key = `${gardenId}:${zoneId}`;
    const newSelected = new Set(selectedZones);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedZones(newSelected);
  };

  const handleToggleAllZonesInGarden = (gardenId: string) => {
    const garden = gardens.find(g => g.id === gardenId);
    if (!garden) return;
    
    const gardenZoneKeys = garden.zones.map(z => `${gardenId}:${z.id}`);
    const allSelected = gardenZoneKeys.every(k => selectedZones.has(k));
    
    const newSelected = new Set(selectedZones);
    if (allSelected) {
      gardenZoneKeys.forEach(k => newSelected.delete(k));
    } else {
      gardenZoneKeys.forEach(k => newSelected.add(k));
    }
    setSelectedZones(newSelected);
  };

  const isGardenFullySelected = (gardenId: string) => {
    const garden = gardens.find(g => g.id === gardenId);
    if (!garden || garden.zones.length === 0) return false;
    return garden.zones.every(z => selectedZones.has(`${gardenId}:${z.id}`));
  };

  const isGardenPartiallySelected = (gardenId: string) => {
    const garden = gardens.find(g => g.id === gardenId);
    if (!garden) return false;
    const selectedCount = garden.zones.filter(z => selectedZones.has(`${gardenId}:${z.id}`)).length;
    return selectedCount > 0 && selectedCount < garden.zones.length;
  };

  const handleConfirm = () => {
    // Parse selected zones
    const zoneSelections: ZoneSelection[] = Array.from(selectedZones).map(key => {
      const [gardenId, zoneId] = key.split(':');
      return { gardenId, zoneId };
    });

    // For each garden, for each selected zone, assign the selected schedules
    const updatedGardens = gardens.map((garden) => ({
      ...garden,
      zones: garden.zones.map((zone) => {
        const isSelected = zoneSelections.some(
          s => s.gardenId === garden.id && s.zoneId === zone.id
        );
        
        if (!isSelected) return zone;

        const newScheduleAssignments: ZoneScheduleAssignment[] = [];
        selectedScheduleIds.forEach((scheduleId) => {
          zone.controllerAssignments.forEach((ca) => {
            newScheduleAssignments.push({
              id: `sa-mass-${Date.now()}-${scheduleId}-${ca.id}`,
              scheduleId,
              controllerId: ca.controllerId,
              valveChannel: ca.valveChannel,
            });
          });
        });
        return {
          ...zone,
          scheduleAssignments: [
            ...(zone.scheduleAssignments || []),
            ...newScheduleAssignments.filter(
              (nsa) =>
                !zone.scheduleAssignments?.some(
                  (esa) =>
                    esa.scheduleId === nsa.scheduleId &&
                    esa.controllerId === nsa.controllerId &&
                    esa.valveChannel === nsa.valveChannel
                )
            ),
          ],
        };
      }),
    }));

    onAssign(updatedGardens);
    setSelectedScheduleIds(new Set());
    setSelectedZones(new Set());
    onOpenChange(false);
  };

  const handleClose = () => {
    setSelectedScheduleIds(new Set());
    setSelectedZones(new Set());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Asignación Masiva de Programaciones
          </DialogTitle>
          <DialogDescription>
            Selecciona los jardines, zonas y programaciones a asignar
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left column: Gardens and Zones */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <MapPin className="w-4 h-4 text-primary" />
              Seleccionar Jardines y Zonas
            </div>
            <ScrollArea className="h-[300px] pr-2 border rounded-lg">
              <Accordion type="multiple" className="w-full">
                {gardens.map((garden) => (
                  <AccordionItem key={garden.id} value={garden.id}>
                    <AccordionTrigger className="px-3 py-2 hover:no-underline">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={isGardenFullySelected(garden.id)}
                          ref={(el) => {
                            if (el) {
                              (el as any).indeterminate = isGardenPartiallySelected(garden.id);
                            }
                          }}
                          onCheckedChange={() => handleToggleAllZonesInGarden(garden.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="text-left">
                          <span className="font-medium text-sm">{garden.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            ({garden.zones.length} zonas)
                          </span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-2">
                      <div className="space-y-1 pl-8">
                        {garden.zones.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-2">
                            Sin zonas configuradas
                          </p>
                        ) : (
                          garden.zones.map((zone) => (
                            <div
                              key={zone.id}
                              className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/50 cursor-pointer"
                              onClick={() => handleToggleZone(garden.id, zone.id)}
                            >
                              <Checkbox
                                checked={selectedZones.has(`${garden.id}:${zone.id}`)}
                                onCheckedChange={() => handleToggleZone(garden.id, zone.id)}
                              />
                              <Layers className="w-3 h-3 text-muted-foreground" />
                              <span className="text-sm">{zone.name}</span>
                              <Badge variant="outline" className="text-xs ml-auto">
                                {zone.controllerAssignments.length} ctrl
                              </Badge>
                            </div>
                          ))
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </ScrollArea>
            {affectedZonesCount > 0 && (
              <p className="text-xs text-muted-foreground">
                {affectedZonesCount} zona(s) seleccionada(s)
              </p>
            )}
          </div>

          {/* Right column: Schedules */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Calendar className="w-4 h-4 text-primary" />
              Seleccionar Programaciones
            </div>
            {activeSchedules.length === 0 ? (
              <div className="py-8 text-center border rounded-lg">
                <Calendar className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  No hay programaciones activas disponibles
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[300px] pr-2 border rounded-lg">
                <div className="space-y-2 p-2">
                  {activeSchedules.map((schedule) => (
                    <div
                      key={schedule.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedScheduleIds.has(schedule.id)
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => handleToggleSchedule(schedule.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedScheduleIds.has(schedule.id)}
                          onCheckedChange={() => handleToggleSchedule(schedule.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{schedule.name}</div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {schedule.startTime}
                            </span>
                            <span className="flex items-center gap-1">
                              <Timer className="w-3 h-3" />
                              {schedule.duration} min
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {formatDays(schedule.daysOfWeek)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
            {selectedScheduleIds.size > 0 && (
              <p className="text-xs text-muted-foreground">
                {selectedScheduleIds.size} programación(es) seleccionada(s)
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedScheduleIds.size === 0 || selectedZones.size === 0}
            className="gap-2"
          >
            <Check className="w-4 h-4" />
            Asignar ({selectedScheduleIds.size} prog. → {affectedZonesCount} zonas)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
